import React from "react"
import { Badge, Card, Text } from "@smooth/design-system-components"
import type { DunningDraftDecision, DunningDraftDecisionObservation } from "../types"
import { terminalShortLabel } from "../terminals"

/**
 * AI ドラフトの「AIの判断記録（自己申告）」。AI が本文を書く前に出力した判断の道筋を、
 * 問い → 答え、根拠、理由の順に読めるように並べ、最後に着地点と要約を出す。
 * 判断の正しさはコードで検証していない（形だけ検査して保存）ため、自己申告であることを明示する。
 * 新規 UI のため design system の部品と semantic トークンで組む（design_system/POLICY.md）。
 * 記録のない旧ドラフトでは何も出さない。AI が出したが形の検査に落ちた記録は、旧ドラフトと区別できるよう一言だけ出す。
 */
export const DraftDecisionLog: React.FC<{
  decision: DunningDraftDecision | null | undefined
  /** 形の検査結果。invalid のときだけ「表示できない」旨を出す */
  observation?: DunningDraftDecisionObservation | null
  /** 初期表示で開くか。既定は閉じる（ドラフトの操作を押し下げないため） */
  defaultOpen?: boolean
}> = ({ decision, observation, defaultOpen = false }) => {
  if (!decision) {
    if (observation?.status !== "invalid") return null
    return (
      <div data-smooth-ds className="mt-2">
        <Text size="sm" tone="muted">
          AIの判断記録は形が崩れていたため表示できません。
        </Text>
      </div>
    )
  }

  return (
    <details data-smooth-ds open={defaultOpen} className="mt-2">
      <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-caption font-bold text-text-accent">
        <span>AIの判断記録（自己申告）</span>
        <Badge>着地点 {terminalShortLabel(decision.terminal)}</Badge>
      </summary>
      <div className="mt-2">
        <Card variant="outlined">
          {decision.steps.length > 0 ? (
            <ol className="flex flex-col gap-3" aria-label="判断の道筋">
              {decision.steps.map((step, index) => (
                <li key={`${index}-${step.node}`} className="flex flex-col gap-1">
                  <Text size="sm" tone="muted" as="span">
                    {index + 1}. {step.node}
                  </Text>
                  <Text>
                    {step.question} → <strong>{step.answer}</strong>
                  </Text>
                  {step.evidence && (
                    <Text size="sm" tone="muted">
                      根拠：{step.evidence}
                    </Text>
                  )}
                  {step.reason && (
                    <Text size="sm" tone="muted">
                      理由：{step.reason}
                    </Text>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <Text size="sm" tone="muted">
              判断の道筋は記録されていません。
            </Text>
          )}
          <div className="mt-3 flex flex-col gap-1 border-t border-border-default pt-3">
            <Text weight="bold">着地点：{terminalShortLabel(decision.terminal)}</Text>
            {decision.summary && <Text>{decision.summary}</Text>}
            <Text size="sm" tone="muted">
              AI が出力した自己申告です。判断の正しさはシステムでは確かめていません。
            </Text>
          </div>
        </Card>
      </div>
    </details>
  )
}
